import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Envelope, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ForgotPasswordDialog — modal launched from the login page's
 * "Lupa password?" link. Collects an email and hits
 * `/api/auth/forgot-password`. The backend always returns 200 (to prevent
 * user enumeration) so we can show the same confirmation regardless of
 * whether the email exists.
 */
export default function ForgotPasswordDialog({ open, onOpenChange, prefillEmail = "" }) {
  const [email, setEmail] = useState(prefillEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Keep the local email in sync when the parent pre-fills a new value
  // (e.g. after user typed something into the main login form).
  React.useEffect(() => {
    if (open) {
      setEmail(prefillEmail);
      setSent(false);
    }
  }, [open, prefillEmail]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed.includes("@")) {
      toast.error("Format email tidak valid");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: trimmed });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Envelope size={20} weight="bold" /> Reset Password
          </DialogTitle>
          <DialogDescription>
            Kami akan kirim link reset password ke email dan WhatsApp (jika terdaftar). Link berlaku 15 menit.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center space-y-3" data-testid="forgot-password-sent">
            <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
            <div className="font-bold">Cek Email &amp; WhatsApp Anda</div>
            <p className="text-sm text-muted-foreground">
              Jika <span className="font-mono">{email}</span> terdaftar, link reset sudah dikirim. Cek juga folder spam.
              Belum terima setelah 5 menit? Coba ulang atau hubungi admin.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full mt-4 h-11 font-bold uppercase tracking-widest"
              data-testid="forgot-password-close"
            >
              Tutup
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 pt-2" data-testid="forgot-password-form">
            <div>
              <label htmlFor="forgot-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                Email Terdaftar
              </label>
              <div className="relative">
                <Envelope
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  aria-hidden
                />
                <Input
                  id="forgot-email"
                  type="text"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@spb.local"
                  className="h-11 pl-10 rounded-lg"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-lg"
                data-testid="forgot-password-cancel"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-lg h-11 font-bold uppercase tracking-widest"
                data-testid="forgot-password-submit"
              >
                {submitting ? "Mengirim..." : "Kirim Link Reset"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
